import { FC, useEffect, useState } from 'react'
import SVG from 'react-inlinesvg'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { setOverride, SetOverridePayload } from '../../../services/commissionsService'
import { showErrorAlert } from '../../../helpers/alert'
import { handleApiError } from '../../../utils/handleApiError'

export interface OverrideTarget {
  scope: 'partner' | 'experience' | 'engagement'
  targetId: number
  label: string
  currentOverride: string | null
}

interface Props {
  isOpen: boolean
  target: OverrideTarget | null
  onClose: () => void
  onSaved: () => void
}

const genIdempotencyKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? (crypto as any).randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const SetOverrideModal: FC<Props> = ({ isOpen, target, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!isOpen || !target) return
    setIdempotencyKey(genIdempotencyKey())
    setNewValue(target.currentOverride ?? '')
    setReason('')
  }, [isOpen, target])

  if (!isOpen || !target) return null

  const validate = () => {
    if (newValue !== '') {
      const n = Number(newValue)
      if (isNaN(n) || n < 0 || n > 100) return 'New rate must be a number between 0 and 100.'
    }
    if (reason.trim().length < 10) return 'Reason must be at least 10 characters.'
    return null
  }

  const handleSave = async () => {
    const error = validate()
    if (error) {
      showErrorAlert({ title: 'Validation', message: error })
      return
    }
    setLoading(true)
    try {
      const payload: SetOverridePayload = {
        scope: target.scope,
        targetId: target.targetId,
        newValue: newValue === '' ? null : newValue,
        reason: reason.trim(),
        idempotencyKey,
      }
      await setOverride(payload)
      onSaved()
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({ title: 'Error', message: 'Could not save the override.' }),
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='nb-modal-overlay' onClick={onClose}>
      <div className='nb-invite-modal' onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className='admin-experience-details-header'>
          <h2 className='nb-invite-modal__title'>
            {target.currentOverride == null ? 'Set override' : 'Edit override'}
          </h2>
          <div className='rm-style-3 cursor-pointer' onClick={onClose}>
            <SVG src={toAbsoluteUrl('/media/svg/nobilis/closeX.svg')} className='rm-style-4' />
          </div>
        </div>

        <div className='team-admin-add-member__card'>
          <div className='form-field'>
            <label className='field-label'>Scope</label>
            <div className='input' style={{ background: '#f5f5f5' }}>
              {target.scope}: {target.label}
            </div>
          </div>

          <div className='form-field'>
            <label className='field-label'>New rate (%) — leave empty to clear</label>
            <input
              type='number'
              className='input'
              step='0.01'
              min={0}
              max={100}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder='(clear override)'
            />
          </div>

          <div className='form-field'>
            <label className='field-label'>Reason / note</label>
            <textarea
              className='input'
              rows={3}
              maxLength={500}
              minLength={10}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='Why is this changing? (min 10 chars)'
            />
          </div>
        </div>

        <div className='form-actions'>
          <button type='button' className='btn-secondary' onClick={onClose}>
            Cancel
          </button>
          <button type='button' className='btn nb-btn-primary' disabled={loading} onClick={handleSave}>
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SetOverrideModal
